import { inject, Injectable } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';

import { AnalyticsService } from './analytics.service';
import { buildRouteName, sanitizePath } from './rum-route.util';

/** Name of the custom event emitted for each SPA route navigation. */
export const ROUTE_NAVIGATION_EVENT_NAME = 'RouteNavigation';

/** Name of the metric tracking SPA route navigation duration (ms). */
export const ROUTE_NAVIGATION_DURATION_METRIC = 'RouteNavigation/Duration';

/** Outcome of a navigation, reported as the `result` dimension. */
type NavigationResult = 'success' | 'cancel' | 'error';

/**
 * Tracks Angular SPA route navigations as RUM telemetry.
 *
 * App Insights' `enableAutoRouteTracking` already emits page views on raw URLs;
 * this adds what that can't: the **stable route template** (`/orders/:id`) for
 * clean grouping, the **navigation duration** measured with `performance.now()`,
 * and explicit capture of **failed/cancelled** navigations (`NavigationError`,
 * `NavigationCancel`) — which auto route tracking ignores but which matter for
 * the freeze/crash investigation.
 *
 * Each navigation emits a `RouteNavigation` event (with `result`, `routeName`,
 * `page`, `durationMs`); successful ones also emit a `RouteNavigation/Duration`
 * metric for percentile aggregation. Mirrors {@link WebVitalsService} in using
 * the {@link AnalyticsService} facade. No-ops when no Router is present.
 */
@Injectable()
export class RouteTrackingService {
  private analytics = inject(AnalyticsService);

  private router = inject(Router, { optional: true });

  private initialized = false;

  /** Navigation start times keyed by the router-assigned navigation id. */
  private startTimes = new Map<number, number>();

  /**
   * Starts tracking navigations. Safe to call multiple times; only the first
   * call subscribes. No-ops without a Router.
   */
  public init(): void {
    if (this.initialized || !this.router) {
      return;
    }
    this.initialized = true;

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.startTimes.set(event.id, performance.now());
      } else if (event instanceof NavigationEnd) {
        this.report(event.id, 'success', sanitizePath(event.urlAfterRedirects));
      } else if (event instanceof NavigationCancel) {
        this.report(event.id, 'cancel', sanitizePath(event.url));
      } else if (event instanceof NavigationError) {
        this.report(event.id, 'error', sanitizePath(event.url), event.error);
      }
    });
  }

  /**
   * Emits the telemetry for a finished navigation and clears its start time.
   * @param id router navigation id
   * @param result navigation outcome
   * @param page sanitized destination path
   * @param error optional error for failed navigations
   */
  private report(
    id: number,
    result: NavigationResult,
    page: string,
    error?: unknown,
  ): void {
    const start = this.startTimes.get(id);
    this.startTimes.delete(id);
    const durationMs =
      start === undefined ? undefined : Math.round(performance.now() - start);

    const properties: Record<string, string | number | undefined> = {
      result,
      routeName: buildRouteName(this.router),
      page,
      durationMs,
    };
    if (error) {
      const message = error instanceof Error ? error.message : String(error);
      properties['error'] = message.slice(0, 300);
    }

    if (result === 'success' && durationMs !== undefined) {
      this.analytics.trackMetric({
        name: ROUTE_NAVIGATION_DURATION_METRIC,
        average: durationMs,
        properties,
      });
    }

    this.analytics.trackEvent({
      name: ROUTE_NAVIGATION_EVENT_NAME,
      properties,
    });
  }
}
